import { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Shield,
  Loader2,
  XCircle,
  CheckCircle2,
  Pencil,
  Trash2,
  X,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  Search,
  Link2,
  Users,
} from "lucide-react";
import { getAPI, postAPI, putAPI, deleteAPI } from "../../api/frameworksApi";
import { getErrorMessage } from "../../api/errorMap";
import { API_URL } from "../../constants";
import {
  Modal,
  ModalShell,
  FormField,
  FormInput,
  FormTextarea,
  FormSelect,
  ModalFooterButtons,
} from "../components/Modal";
import { EmptyState } from "../components/EmptyState";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { RBACAction } from "../components/RBACButton";
import { Tip } from "../components/ui/tooltip";
import { useRBACPermission } from "../../hooks/useRBACPermission";
import {
  PermissionMatrix,
  type AvailablePermission,
} from "../components/PermissionMatrix";

/* ── Module Permissions Configuration ── */
const MODULE_PERMISSIONS_CONFIG: Record<string, Set<string>> = {
  // Full permissions
  document: new Set([
    "create",
    "view",
    "update",
    "delete",
    "export",
    "approve",
    "publish",
  ]),
  evidence: new Set([
    "create",
    "view",
    "update",
    "delete",
    "export",
    "approve",
    "publish",
  ]),
  catalog: new Set([
    "create",
    "view",
    "update",
    "delete",
    "export",
    "approve",
    "publish",
  ]),
  profile: new Set([
    "create",
    "view",
    "update",
    "delete",
    "export",
    "approve",
    "publish",
  ]),
  control_mapping: new Set([
    "create",
    "view",
    "update",
    "delete",
    "export",
    "approve",
    "publish",
  ]),
  component: new Set([
    "create",
    "view",
    "update",
    "delete",
    "export",
    "approve",
    "publish",
  ]),
  ssp: new Set([
    "create",
    "view",
    "update",
    "delete",
    "export",
    "approve",
    "publish",
  ]),

  // Assessment modules
  assessment_plan: new Set(["create", "view", "update", "approve"]),
  assessment_execution: new Set(["view", "update", "approve"]),
  assessment_review: new Set(["view", "update", "approve"]),
  assessment_result: new Set(["view", "update"]),

  // Risk
  poam: new Set(["view", "update", "approve"]),

  // Administration
  organization: new Set(["create", "view", "update", "delete"]),
  user: new Set(["view", "update"]),
  role: new Set(["create", "view", "update", "delete"]),
  role_mapping: new Set(["view", "update"]),
  cmdb: new Set(["create", "view", "update", "delete"]),
  validation_rule: new Set(["create", "view", "update", "delete"]),
  llm_configuration: new Set(["create", "view", "update", "delete"]),

  // Insights - dashboard configuration is view-only
  dashboard_configuration: new Set(["view"]),
};

/* ── Types ─────────────────────────────────────────────── */

interface PlatformRole {
  id: string;
  name: string;
  displayName: string;
  description: string;
  scope: string;
  isSystem: boolean;
  isActive: boolean;
}

interface RoleForm {
  name: string;
  displayName: string;
  description: string;
  scope: string;
}

interface GroupMapping {
  mappingId: string;
  groupName: string;
  groupDn: string;
  orgId: string;
  orgName: string;
  isActive: boolean;
}

interface RoleWithMappings {
  id: string;
  name: string;
  displayName: string;
  scope: string;
  isSystem: boolean;
  groupMappings: GroupMapping[];
}

interface AdGroup {
  dn: string;
  name: string;
  description: string;
  memberDns: string[];
}

interface OrgUnit {
  id: string;
  name: string;
  shortName: string;
}

interface SelectedGroup {
  groupName: string;
  groupDn: string;
}

interface MappingForm {
  groups: SelectedGroup[];
  roleName: string;
  orgId: string;
  syncUsers: boolean;
  inheritChildren: boolean;
}

interface RoleMember {
  id: string;
  email: string;
  displayName: string;
  isActive: boolean;
}

interface MemberSheetContext {
  role: RoleWithMappings;
  mapping: GroupMapping;
}

type ActiveTab = "roles" | "mappings";

const EMPTY_ROLE: RoleForm = {
  name: "",
  displayName: "",
  description: "",
  scope: "org",
};
// Backend requires role names to be lowercase words joined by underscores,
// e.g. compliance_manager — no digits, no leading/trailing/double underscores.
const ROLE_NAME_PATTERN = /^[a-z]+(_[a-z]+)*$/;
// Org-unit types returned by the accessible-orgs API, in display order.
// Labels are properly cased (no forced uppercase per UI guidelines).
const ORG_TYPE_META: { value: string; label: string }[] = [
  { value: "GROUP", label: "Group" },
  { value: "ORGANIZATION", label: "Organization" },
  { value: "DIVISION", label: "Division" },
  { value: "DEPARTMENT", label: "Department" },
  { value: "APPLICATION", label: "Application" },
];

const EMPTY_MAPPING: MappingForm = {
  groups: [],
  roleName: "",
  orgId: "",
  syncUsers: true,
  inheritChildren: true,
};
 
/* ── Component ─────────────────────────────────────────── */

export function RoleManagement() {
  const [activeTab, setActiveTab] = useState<ActiveTab>("roles");

  const [roles, setRoles] = useState<PlatformRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Create-role modal (edit happens on a dedicated page)
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [roleForm, setRoleForm] = useState<RoleForm>(EMPTY_ROLE);
  const [savingRole, setSavingRole] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteRoleTarget, setDeleteRoleTarget] = useState<PlatformRole | null>(
    null,
  );

  const [rolePermCounts, setRolePermCounts] = useState<Record<string, number>>(
    {},
  );

  // Group mappings tab state
  const [rolesWithMappings, setRolesWithMappings] = useState<
    RoleWithMappings[]
  >([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [mappingForm, setMappingForm] = useState<MappingForm>(EMPTY_MAPPING);
  /* Set when the modal is opened from a role's own "Add" action. The role is
   * then fixed for the whole mapping and shown read-only instead of a select. */
  const [lockedRoleLabel, setLockedRoleLabel] = useState<string>("");
  const [savingMapping, setSavingMapping] = useState(false);
  const [adGroups, setAdGroups] = useState<AdGroup[]>([]);
  const [adGroupsLoading, setAdGroupsLoading] = useState(false);
  const [adSearch, setAdSearch] = useState("");
  // Org units for the currently selected org type (loaded on type change).
  const [orgUnits, setOrgUnits] = useState<OrgUnit[]>([]);
  const [selectedOrgType, setSelectedOrgType] = useState<string>("");
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [expandedMappingRoles, setExpandedMappingRoles] = useState<Set<string>>(
    new Set(),
  );

  // Delete-mapping confirmation
  const [deleteMappingTarget, setDeleteMappingTarget] =
    useState<GroupMapping | null>(null);
  const [deletingMapping, setDeletingMapping] = useState(false);

  // Members sheet (per-mapping)
  const [memberSheet, setMemberSheet] = useState<MemberSheetContext | null>(
    null,
  );
  const [members, setMembers] = useState<RoleMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Permissions modal
  const [permTargetRole, setPermTargetRole] = useState<PlatformRole | null>(
    null,
  );
  const [availablePerms, setAvailablePerms] = useState<AvailablePermission[]>(
    [],
  );
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(new Set());
  const [initialPerms, setInitialPerms] = useState<Set<string>>(new Set());
  const [permsLoading, setPermsLoading] = useState(false);
  const [savingPerms, setSavingPerms] = useState(false);

  // Edit role details modal
  const [editTargetRole, setEditTargetRole] = useState<PlatformRole | null>(
    null,
  );
  const [editForm, setEditForm] = useState<{
    displayName: string;
    description: string;
  }>({ displayName: "", description: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  /* ── Fetch ── */

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getAPI(API_URL.PLATFORM_ROLES + "/all");
      setRoles(res?.data ?? []);
    } catch {
      setError("Failed to load roles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const fetchPermCountForRole = useCallback(async (roleId: string) => {
    try {
      const res = await getAPI(
        `${API_URL.PLATFORM_ROLES}/${roleId}/permissions`,
      );
      const count = res?.data?.permissionCodes?.length ?? 0;
      setRolePermCounts((prev) => ({ ...prev, [roleId]: count }));
    } catch {
      // Fail silently - will show "Manage permissions" instead
    }
  }, []);

  const fetchRolesWithMappings = useCallback(async () => {
    setMappingsLoading(true);
    setError("");
    try {
      const res = await getAPI(API_URL.PLATFORM_ROLES_WITH_MAPPINGS);
      setRolesWithMappings(res?.data ?? []);
    } catch {
      setError("Failed to load mappings");
    } finally {
      setMappingsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRolesWithMappings();
  }, [fetchRolesWithMappings]);

  // Fetch members whenever the member sheet opens for a different mapping
  useEffect(() => {
    if (!memberSheet) return;
    let cancelled = false;
    (async () => {
      setMembersLoading(true);
      setMembers([]);
      try {
        const params = new URLSearchParams({
          pageSize: "200",
          role: memberSheet.role.name,
          org_id: memberSheet.mapping.orgId,
        });
        const res = await getAPI(`${API_URL.ADMIN_USERS}?${params}`);
        if (cancelled) return;
        setMembers(res?.data ?? []);
      } catch {
        if (!cancelled) setMembers([]);
      } finally {
        if (!cancelled) setMembersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [memberSheet]);

  // Fetch permissions when permissions modal opens for a role
  useEffect(() => {
    if (!permTargetRole) return;
    let cancelled = false;
    (async () => {
      setPermsLoading(true);
      setError("");
      try {
        const [availRes, roleRes] = await Promise.all([
          getAPI(API_URL.AVAILABLE_PERMISSIONS),
          getAPI(`${API_URL.PLATFORM_ROLES}/${permTargetRole.id}/permissions`),
        ]);
        if (cancelled) return;
        setAvailablePerms(availRes?.data ?? []);
        const codes = new Set<string>(roleRes?.data?.permissionCodes ?? []);
        setSelectedPerms(codes);
        setInitialPerms(new Set(codes));
        // Cache the permission count for this role
        setRolePermCounts((prev) => ({
          ...prev,
          [permTargetRole.id]: codes.size,
        }));
      } catch {
        if (!cancelled) {
          setError("Failed to load permissions");
          setPermTargetRole(null);
        }
      } finally {
        if (!cancelled) setPermsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [permTargetRole]);

  const fetchAdGroups = async (search?: string) => {
    setAdGroupsLoading(true);
    try {
      const url = search?.trim()
        ? `${API_URL.LDAP_GROUPS}/search?pattern=${encodeURIComponent(search)}`
        : API_URL.LDAP_GROUPS;
      const res = await getAPI(url);
      setAdGroups(res?.data ?? []);
    } catch {
      setAdGroups([]);
    } finally {
      setAdGroupsLoading(false);
    }
  };

  const fetchOrgs = async (org_type: string) => {
    setOrgsLoading(true);
    try {
      // Scope to the orgs the current user can actually create role mappings in
      // (module=role_mapping, action=create), filtered to the chosen org type
      // (e.g. org_type=APPLICATION) — same pattern Catalogs / Mappings / SSP use.
      const res = await getAPI(
        `${API_URL.ACCESSIBLE_ORGS}?module=role_mapping&action=create&org_type=${org_type}`,
      );
      // Response is a flat array of org units already scoped to the type.
      setOrgUnits(res?.data?.organizations ?? []);
    } catch {
      setOrgUnits([]);
    } finally {
      setOrgsLoading(false);
    }
  };

  /* ── Handlers ── */

  const flash = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 3000);
  };

  const openCreateRole = () => {
    setRoleForm(EMPTY_ROLE);
    setError("");
    setShowCreateModal(true);
  };

  const handleCreateRole = async () => {
    if (!roleForm.name.trim() || !roleForm.displayName.trim()) return;
    setSavingRole(true);
    setError("");
    try {
      await postAPI(API_URL.PLATFORM_ROLES, roleForm);
      flash(`Role "${roleForm.displayName}" created`);
      setShowCreateModal(false);
      await fetchRoles();
    } catch (err: any) {
      setError(getErrorMessage(err, "Failed to create role"));
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRole = async (role: PlatformRole) => {
    if (role.isSystem) return;
    setDeletingId(role.id);
    setError("");
    try {
      await deleteAPI(`${API_URL.PLATFORM_ROLES}/${role.id}`);
      flash(`Role "${role.displayName}" deleted`);
      await fetchRoles();
      setDeleteRoleTarget(null);
    } catch (err: any) {
      setError(getErrorMessage(err, "Failed to delete role"));
    } finally {
      setDeletingId(null);
    }
  };

  // Permissions handlers
  const openPermissions = (role: PlatformRole) => setPermTargetRole(role);

  const handleSavePerms = async () => {
    if (!permTargetRole) return;
    setSavingPerms(true);
    setError("");
    try {
      await putAPI(
        `${API_URL.PLATFORM_ROLES}/${permTargetRole.id}/permissions`,
        {
          roleId: permTargetRole.id,
          permissionCodes: Array.from(selectedPerms),
        },
      );
      flash(`Permissions updated for "${permTargetRole.displayName}"`);
      // Update count for just this role
      await fetchPermCountForRole(permTargetRole.id);
      setPermTargetRole(null);
    } catch (err: any) {
      setError(getErrorMessage(err, "Failed to save permissions"));
    } finally {
      setSavingPerms(false);
    }
  };

  const isPermsDirty =
    selectedPerms.size !== initialPerms.size ||
    [...selectedPerms].some((c) => !initialPerms.has(c));

  // Edit-details handlers
  const openEditRole = (role: PlatformRole) => {
    setEditForm({
      displayName: role.displayName,
      description: role.description ?? "",
    });
    setEditTargetRole(role);
  };

  const handleEditSave = async () => {
    if (!editTargetRole || !editForm.displayName.trim()) return;
    setSavingEdit(true);
    setError("");
    try {
      await putAPI(`${API_URL.PLATFORM_ROLES}/${editTargetRole.id}`, {
        name: editTargetRole.name,
        displayName: editForm.displayName.trim(),
        description: editForm.description.trim(),
        scope: editTargetRole.scope,
      });
      flash(`Role "${editForm.displayName}" updated`);
      setEditTargetRole(null);
      await fetchRoles();
    } catch (err: any) {
      setError(getErrorMessage(err, "Failed to update role"));
    } finally {
      setSavingEdit(false);
    }
  };

  // Mapping handlers
  const openAddMapping = (preselectedRole?: string, roleLabel?: string) => {
    setMappingForm({ ...EMPTY_MAPPING, roleName: preselectedRole ?? "" });
    setLockedRoleLabel(preselectedRole ? (roleLabel ?? preselectedRole) : "");
    setAdSearch("");
    setAdGroups([]);
    setSelectedOrgType("");
    setOrgUnits([]);
    setShowMappingModal(true);
    fetchAdGroups();
  };

  // Org units for the selected type, mapped to dropdown options.
  const orgOptions = orgUnits.map((o) => ({
    value: o.id,
    label: o.shortName ? `${o.name} (${o.shortName})` : o.name,
  }));

  // Label the Organization field with the chosen type (e.g. "Application").
  const orgTypeLabel =
    ORG_TYPE_META.find((t) => t.value === selectedOrgType)?.label ??
    "Organization";

  const handleOrgTypeChange = (type: string) => {
    setSelectedOrgType(type);
    // Clear any previously chosen organization and load orgs for the new type.
    setMappingForm((f) => (f.orgId ? { ...f, orgId: "" } : f));
    setOrgUnits([]);
    if (type) fetchOrgs(type);
  };

  const toggleAdGroup = (group: AdGroup) => {
    setMappingForm((f) => {
      const exists = f.groups.some((g) => g.groupDn === group.dn);
      return {
        ...f,
        groups: exists
          ? f.groups.filter((g) => g.groupDn !== group.dn)
          : [...f.groups, { groupName: group.name, groupDn: group.dn }],
      };
    });
  };

  const handleCreateMapping = async () => {
    if (
      !mappingForm.groups.length ||
      !mappingForm.orgId ||
      !mappingForm.roleName
    )
      return;
    setSavingMapping(true);
    setError("");
    try {
      await postAPI(API_URL.GROUP_MAPPINGS, {
        groups: mappingForm.groups,
        roleName: mappingForm.roleName,
        orgId: mappingForm.orgId,
        syncUsers: mappingForm.syncUsers,
        inheritChildren: mappingForm.inheritChildren,
      });
      setShowMappingModal(false);
      flash(
        `${mappingForm.groups.length} group${mappingForm.groups.length > 1 ? "s" : ""} mapped`,
      );
      await fetchRolesWithMappings();
    } catch (err: any) {
      setError(getErrorMessage(err, "Failed to create mapping"));
    } finally {
      setSavingMapping(false);
    }
  };

  const handleDeleteMapping = async (mappingId: string) => {
    setDeletingMapping(true);
    setError("");
    try {
      await deleteAPI(`${API_URL.GROUP_MAPPINGS}/${mappingId}`);
      flash("Mapping removed");
      await fetchRolesWithMappings();
      setDeleteMappingTarget(null);
    } catch (err: any) {
      setError(getErrorMessage(err, "Failed to remove mapping"));
    }
  };

  const toggleMappingRole = (id: string) => {
    setExpandedMappingRoles((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const expandAllMappings = () => {
    setExpandedMappingRoles(new Set(rolesWithMappings.map((r) => r.id)))
  };

  const collapseAllMappings = () => {
    setExpandedMappingRoles(new Set());
  };

  const allRolesExpanded =
    rolesWithMappings.length > 0 &&
    rolesWithMappings.every((r) => expandedMappingRoles.has(r.id));

  const totalMappings = rolesWithMappings.reduce(
    (s, r) => s + (r.groupMappings?.length ?? 0),
    0,
  );

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1>Roles</h1>
          <p className="text-sm d mt-0.5">
            Define platform roles, permissions, and AD group mappings
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
          {activeTab === "roles" ? (
            <RBACAction module="role" action="create">
              <button
                onClick={openCreateRole}
                className="h-9 flex items-center gap-2 px-4 rounded-xl bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] dark:shadow-[0_4px_14px_rgba(139,92,246,0.3)] hover:-translate-y-px"
              >
                <Plus className="size-4" /> New Role
              </button>
            </RBACAction>
          ) : (
            <RBACAction module="role_mapping" action="create">
              <button
                onClick={() => openAddMapping()}
                className="h-9 flex items-center gap-2 px-4 rounded-xl bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] dark:shadow-[0_4px_14pt_rgba(139,92,246,0.3)] hover:-translate-y-px"
              >
                <Plus className="size-4" /> New Mapping
              </button>
            </RBACAction>
          )}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex items-center gap-1 p-1 bg-muted/50 dark:bg-white/[0.03] rounded-lg w-fit">
        <button
          onClick={() => setActiveTab("roles")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${
            activeTab === "roles"
              ? "bg-background dark:bg-white/[0.08] text-foreground shadow-sm"
              : "d hover:text-foreground hover:bg-background/50 dark:hover:bg-white/[0.04]"
          }`}
        >
          <Shield className="size-4" />
          Roles
          <span className="text-xs px-1.5 py-0.5 rounded-full bg-muted d font-medium">
            {roles.length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab("mappings")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${
            activeTab === "mappings"
              ? "bg-background dark:bg-white/[0.08] text-foreground shadow-sm"
              : "d hover:text-foreground hover:bg-background/50 dark:hover:bg-white/[0.04]"
          }`}
        >
          <Link2 className="size-4" />
          Group Mappings
          <span className="text-xs px-1.5 py-0.5 rounded-full bg-muted d font-medium">
            {totalMappings}
          </span>
        </button>
      </div>

      {/* ── Alerts ── */}
      
      {success && (
        <div className="flex items-center gap-2.5 px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-sm text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="size-4 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* ── Roles tab ── */}
      {activeTab === "roles" &&
        (loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="size-5 animate-spin d" />
          </div>
        ) : roles.length === 0 ? (
          <EmptyState
            icon={Shield}
            title="No roles configured"
            description="Create platform roles and assign permissions to control access."
            action={
              <button
                onClick={openCreateRole}
                className="h-9 flex items-center gap-2 px-4 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-all"
              >
                <Plus className="size-4" /> New Role
              </button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {roles.map((role) => (
              <div
                key={role.id}
                role="button"
                tabIndex={0}
                onClick={() => openPermissions(role)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") openPermissions(role);
                }}
                className="rounded-lg border border-border bg-card overflow-hidden group hover:shadow-md hover:border-indigo-200/40 dark:hover:border-indigo-500/15 transition-all flex flex-col cursor-pointer"
              >
                <div className="px-4 pt-4 pb-3 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className="size-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-indigo-50 dark:bg-indigo-500/10">
                        <Shield className="size-4 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold leading-tight">
                          {role.displayName}
                        </h3>
                        <p className="text-[11px] d font-mono mt-0.5">
                          {role.name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Tip label="Role Permissions">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openPermissions(role);
                          }}
                          className="p-1.5 rounded-md d hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 transition-colors"
                        >
                          <ShieldCheck className="size-3.5" />
                        </button>
                      </Tip>
                      <RBACAction module="role" action="edit">
                        <Tip label="Edit Role">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditRole(role);
                            }}
                            className="p-1.5 rounded-md d hover:text-foreground hover:bg-accent transition-colors"
                          >
                            <Pencil className="size-3.5" />
                          </button>
                        </Tip>
                      </RBACAction>
                      {!role.isSystem && (
                        <RBACAction module="role" action="delete">
                          <Tip label="Delete Role">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteRoleTarget(role);
                              }}
                              disabled={deletingId === role.id}
                              className="p-1.5 rounded-md d hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-40"
                            >
                              {deletingId === role.id ? (
                                <Loader2 className="size-3.5 animate-spin" />
                              ) : (
                                <Trash2 className="size-3.5" />
                              )}
                            </button>
                          </Tip>
                        </RBACAction>
                      )}
                    </div>
                  </div>

                  {/* Badges */}
                  <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                    <span
                      className={`text-[10px] w-16 text-center py-0.5 rounded-full font-bold ring-1 ${
                        role.isSystem
                          ? "bg-slate-100 dark:bg-white/[0.06] text-slate-600 dark:text-slate-400 ring-slate-200/60 dark:ring-white/[0.08]"
                          : "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 ring-amber-200/60 dark:ring-amber-500/20"
                      }`}
                    >
                      {role.isSystem ? "Default" : "Custom"}
                    </span>
                    {!role.isActive && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 font-semibold">
                        Inactive
                      </span>
                    )}
                  </div>

                  {role.description && (
                    <p className="text-xs d mt-2.5 line-clamp-2 leading-relaxed">
                      {role.description}
                    </p>
                  )}
                </div>

                {/* Footer: permissions count (clickable area) */}
                <div className="border-t border-border bg-muted/20 dark:bg-white/[0.01] px-4 py-2.5 flex items-center justify-between gap-2 group-hover:bg-indigo-50/40 dark:group-hover:bg-indigo-500/[0.04] transition-colors">
                  <div className="flex items-center gap-1.5 text-xs d">
                    <ShieldCheck className="size-3.5" />
                    <span className="font-medium">
                      {rolePermCounts[role.id] !== undefined
                        ? `${rolePermCounts[role.id]} permission${rolePermCounts[role.id] !== 1 ? "s" : ""} assigned`
                        : "Manage permissions"}
                    </span>
                  </div>
                  <span className="text-[10px] d/0 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 font-semibold transition-colors flex items-center gap-1">
                    View & edit <ChevronRight className="size-3" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        ))}

      {/* ── Group Mappings tab ── */}
      {activeTab === "mappings" &&
        (mappingsLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="size-5 animate-spin d" />
          </div>
        ) : rolesWithMappings.length === 0 ? (
          <EmptyState
            icon={Link2}
            title="No roles available"
            description="Create roles first, then come back to map AD groups."
          />
        ) : (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-[#f8fafc] dark:bg-white/[0.02]">
              <div className="flex items-center gap-2">
                <Link2 className="size-4 d" />
                <span className="text-xs font-bold d tracking-wider">
                  Role → AD Group Assignments
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-muted d font-medium">
                  {totalMappings} total
                </span>
              </div>
              <button
                onClick={() =>
                  allRolesExpanded ? collapseAllMappings() : expandAllMappings()
                }
                className="text-xs d hover:text-foreground transition-colors"
              >
                {allRolesExpanded ? "Collapse all" : "Expand all"}
              </button>
            </div>

            <div className="divide-y divide-border">
              {rolesWithMappings.map((role) => {
                const mappings = role.groupMappings || [];
                const isExpanded = expandedMappingRoles.has(role.id);
                const hasMappings = mappings.length > 0;

                return (
                  <div key={role.id}>
                    <button
                      onClick={() => toggleMappingRole(role.id)}
                      className={`w-full grid grid-cols-[20px_32px_1fr_90px_64px] gap-3 items-center px-5 py-3 text-left transition-colors hover:bg-accent/30 ${
                        isExpanded && hasMappings
                          ? "bg-accent/20 dark:bg-white/[0.02]"
                          : ""
                      }`}
                    >
                      <span className="d">
                        {isExpanded ? (
                          <ChevronDown className="size-4" />
                        ) : (
                          <ChevronRight className="size-4" />
                        )}
                      </span>
                      <div className="size-8 rounded-lg flex items-center justify-center bg-indigo-50 dark:bg-indigo-500/10">
                        <Shield className="size-3.5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <span className="text-sm font-semibold truncate">
                        {role.displayName}
                      </span>
                      <span
                        className={`text-xs w-[90px] text-center py-0.5 rounded-full font-medium ${
                          hasMappings
                            ? "bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-400"
                            : "bg-muted d/50"
                        }`}
                      >
                        {mappings.length} group
                        {mappings.length !== 1 ? "s" : ""}
                      </span>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          openAddMapping(role.name, role.displayName);
                        }}
                        className="flex items-center justify-center gap-1 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors px-2 py-1 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                      >
                        <Plus className="size-3.5" /> Add
                      </span>
                    </button>

                    {isExpanded && (
                      <div className="bg-muted/15 dark:bg-white/[0.01]">
                        {hasMappings ? (
                          <div>
                            {mappings.map((m, idx) => {
                              const groupShort = m.groupName
                                .split(",")[0]
                                .replace(/^[Cc][Nn]=/, "");
                              return (
                                <div
                                  key={m.mappingId}
                                  className="flex items-center gap-3 pl-16 pr-5 py-2.5 hover:bg-accent/30 transition-colors relative"
                                >
                                  <div
                                    className="absolute left-[2.15rem] top-0 w-px bg-border dark:bg-white/[0.06]"
                                    style={{
                                      height:
                                        idx === mappings.length - 1
                                          ? "50%"
                                          : "100%",
                                    }}
                                  />
                                  <div className="absolute left-[2.15rem] top-1/2 h-px bg-border dark:bg-white/[0.06] w-4" />
                                  <div className="size-7 rounded-lg bg-teal-50 dark:bg-teal-500/10 flex items-center justify-center flex-shrink-0 relative z-10">
                                    <Users className="size-3 text-teal-600 dark:text-teal-400" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">
                                      {groupShort}
                                    </p>
                                    <p
                                      className="text-[10px] d font-mono truncate"
                                      title={m.groupDn}
                                    >
                                      {m.groupDn}
                                    </p>
                                  </div>
                                  <span className="text-[11px] px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 font-medium border border-amber-200/40 dark:border-amber-500/15 flex-shrink-0">
                                    {m.orgName}
                                  </span>
                                  <Tip label="View Members" className="inline-flex flex-shrink-0">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setMemberSheet({ role, mapping: m });
                                      }}
                                      className="p-1.5 rounded-md d hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors flex-shrink-0"
                                    >
                                      <Users className="size-3.5" />
                                    </button>
                                  </Tip>
                                  <RBACAction
                                    module="role_mapping"
                                    action="delete"
                                  >
                                    <Tip label="Remove Group Mapping" className="inline-flex flex-shrink-0">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setDeleteMappingTarget(m);
                                        }}
                                        className="p-1.5 rounded-md d/30 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors flex-shrink-0"
                                      >
                                        <Trash2 className="size-3.5" />
                                      </button>
                                    </Tip>
                                  </RBACAction>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="pl-16 pr-5 py-4 text-sm d">
                            No groups mapped
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

      {/* ── Create Role Modal ── */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Role"
        description="Define a new platform role. You'll set permissions and group mappings on the next screen."
        footer={
          <ModalFooterButtons
            onCancel={() => setShowCreateModal(false)}
            onSubmit={handleCreateRole}
            submitLabel={savingRole ? "Saving" : "Save"}
            submitDisabled={
              savingRole ||
              !roleForm.name.trim() ||
              !roleForm.displayName.trim() ||
              !ROLE_NAME_PATTERN.test(roleForm.name.trim())
            }
          />
        }
      >
        {error && (
          <div className="flex items-center gap-2.5 px-4 py-3 mb-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-600 dark:text-red-400">
            <XCircle className="size-4 flex-shrink-0" />
            {error}
            <button
              onClick={() => setError("")}
              className="ml-auto p-0.5 hover:opacity-70"
            >
              <X className="size-3.5" />
            </button>
          </div>
        )}
        <FormField
          label="Role Name"
          required
          error={
            roleForm.name && !ROLE_NAME_PATTERN.test(roleForm.name)
              ? "Lowercase letters and underscores only (e.g. compliance_manager)"
              : undefined
          }
        >
          <FormInput
            placeholder="e.g. compliance_manager"
            value={roleForm.name}
            onChange={(v) => {
              setRoleForm((f) => ({ ...f, name: v }));
              if (error) setError("");
            }}
          />
        </FormField>
        <FormField label="Display Name" required>
          <FormInput
            placeholder="e.g. Compliance Manager"
            value={roleForm.displayName}
            onChange={(v) => setRoleForm((f) => ({ ...f, displayName: v }))}
          />
        </FormField>
        <FormField label="Description">
          <FormTextarea
            placeholder="Describe what this role can do"
            value={roleForm.description}
            onChange={(v) => setRoleForm((f) => ({ ...f, description: v }))}
          />
        </FormField>
      </Modal>

      {/* ── Add Mapping Modal ── */}
      <Modal
        open={showMappingModal}
        onClose={() => setShowMappingModal(false)}
        title="Map AD Group to Role"
        size="lg"
        description={
          lockedRoleLabel
            ? "Select an organization and AD groups to map to this role"
            : "Select a role, organization, and AD groups to map"
        }
        footer={
          <ModalFooterButtons
            onCancel={() => setShowMappingModal(false)}
            onSubmit={handleCreateMapping}
            submitLabel={savingMapping ? "Saving" : "Save"}
            submitDisabled={
              savingMapping ||
              !mappingForm.groups.length ||
              !mappingForm.orgId ||
              !mappingForm.roleName
            }
          />
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Role" required>
            {lockedRoleLabel ? (
              /* Opened from a specific role: the role is fixed, so show it as a
               * read-only value rather than asking the user to pick it again. */
              <div className="w-full flex items-center gap-2 text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 border border-[#e2e8f0] dark:border-white/[0.07] text-[#0f172a] dark:text-white">
                <Shield className="size-3.5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                <span className="font-medium break-words">
                  {lockedRoleLabel}
                </span>
              </div>
            ) : (
              <FormSelect
                placeholder="Select role"
                options={roles.map((r) => ({
                  value: r.name,
                  label: r.displayName,
                }))}
                value={mappingForm.roleName}
                onChange={(v) => setMappingForm((f) => ({ ...f, roleName: v }))}
              />
            )}
          </FormField>

          <FormField label="Organization Type" required>
            <FormSelect
              placeholder="Select type"
              options={ORG_TYPE_META}
              value={selectedOrgType}
              onChange={handleOrgTypeChange}
            />
          </FormField>

          <div className="col-span-2">
            <FormField label={orgTypeLabel} required>
              {orgsLoading ? (
                <div className="flex items-center gap-2 py-2">
                  <Loader2 className="size-4 animate-spin d" />
                  <span className="text-sm d">Loading</span>
                </div>
              ) : (
                <FormSelect
                  searchable
                  placeholder={
                    selectedOrgType
                      ? `Select ${orgTypeLabel.toLowerCase()}`
                      : "Select a type first"
                  }
                  options={orgOptions}
                  value={mappingForm.orgId}
                  onChange={(v) => setMappingForm((f) => ({ ...f, orgId: v }))}
                />
              )}
            </FormField>
          </div>
        </div>

        <FormField label="AD Groups" required>
          <div className="space-y-2.5">
            <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] bg-[#f5f7fb] dark:bg-white/[0.04] focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/10 transition-all">
              {mappingForm.groups.length > 0 && (
                <div className="flex flex-wrap gap-1.5 px-3 pt-2.5 pb-1">
                  {error && (
                      <div className="flex items-center gap-2.5 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-600 dark:text-red-400">
                        <XCircle className="size-4 flex-shrink-0" />
                        {error}
                        <button
                            onClick={() => setError("")}
                            className="ml-auto p-0.5 hover:opacity-70"
                        >
                          <X className="size-3.5" />
                        </button>
                      </div>
                  )}
                  {mappingForm.groups.map((g) => (
                    <span
                      key={g.groupDn}
                      className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md bg-indigo-100 dark:bg-indigo-500/20 text-xs font-medium text-indigo-700 dark:text-indigo-300"
                    >
                      <Users className="size-3 flex-shrink-0" />
                      {g.groupName}
                      <button
                        onClick={() =>
                          toggleAdGroup({
                            dn: g.groupDn,
                            name: g.groupName,
                            description: "",
                            memberDns: [],
                          })
                        }
                        className="p-0.5 rounded hover:bg-indigo-200 dark:hover:bg-indigo-500/30 transition-colors ml-0.5"
                      >
                        <X className="size-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d pointer-events-none" />
                <input
                  type="text"
                  value={adSearch}
                  onChange={(e) => setAdSearch(e.target.value)}
                  placeholder={
                    mappingForm.groups.length > 0
                      ? "Add more groups"
                      : "Search AD groups"
                  }
                  className="w-full text-sm bg-transparent pl-9 pr-3 py-2.5 outline-none placeholder:text-[#94a3b8] dark:placeholder:text-white/25"
                />
              </div>
            </div>

            {(adGroupsLoading || adGroups.length > 0) && (
              <div className="max-h-[min(13rem,28vh)] overflow-y-auto rounded-lg border border-border bg-background shadow-lg">
                {adGroupsLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="size-4 animate-spin d" />
                  </div>
                ) : (() => {
                  const q = adSearch.trim().toLowerCase();
                  const list = q
                    ? adGroups.filter(
                        (g) =>
                          g.name?.toLowerCase().includes(q) ||
                          g.description?.toLowerCase().includes(q),
                      )
                    : adGroups;
                  if (list.length === 0) {
                    return (
                      <div className="px-3 py-6 text-center text-sm d">
                        No groups match your search
                      </div>
                    );
                  }
                  return list.map((g) => {
                    const isSelected = mappingForm.groups.some(
                      (s) => s.groupDn === g.dn,
                    );
                    return (
                      <button
                        key={g.dn}
                        type="button"
                        onClick={() => toggleAdGroup(g)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors border-b border-border/50 last:border-b-0 ${
                          isSelected
                            ? "bg-indigo-50 dark:bg-indigo-500/10"
                            : "hover:bg-accent/50"
                        }`}
                      >
                        <div
                          className={`size-4 rounded flex items-center justify-center flex-shrink-0 border transition-colors ${
                            isSelected
                              ? "bg-indigo-600 border-indigo-600 text-white"
                              : "border-border bg-background"
                          }`}
                        >
                          {isSelected && <CheckCircle2 className="size-3" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-sm font-medium truncate ${isSelected ? "text-indigo-700 dark:text-indigo-300" : ""}`}
                          >
                            {g.name}
                          </p>
                        </div>
                        {g.memberDns?.length > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted d flex-shrink-0">
                            {g.memberDns.length} members
                          </span>
                        )}
                      </button>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        </FormField>
      </Modal>

      {/* ── Permissions Modal ── */}
      <ModalShell
        open={!!permTargetRole}
        onClose={() => setPermTargetRole(null)}
        variant="wide"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-5 border-b border-border flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-foreground leading-tight text-start">
              {permTargetRole
                ? `Permissions - ${permTargetRole.displayName}`
                : "Permissions"}
            </h2>
            <p className="text-sm d mt-2 text-start">
              Select the actions this role can perform across each module.
              Changes apply to everyone holding this role.
            </p>
          </div>
          <button
            onClick={() => setPermTargetRole(null)}
            className="flex-shrink-0 p-1.5 -mr-1.5 rounded-md d hover:text-foreground hover:bg-accent transition-colors"
            aria-label="Close"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Body */}
        {permsLoading ? (
          <div className="flex-1 flex items-center justify-center py-16">
            <Loader2 className="size-5 animate-spin d" />
          </div>
        ) : (
          <div className="flex-1 min-h-0 p-5 flex flex-col">
            <PermissionMatrix
              availablePermissions={availablePerms}
              selected={selectedPerms}
              onChange={setSelectedPerms}
            />
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-5 border-t border-border flex-shrink-0 bg-card">
          {(() => {
            // Calculate supported permissions count
            // report_configuration is hidden from the matrix in the FE, so
            // keep it out of the count to match what the user can actually see.
            const supportedPerms = availablePerms.filter((p) => {
              if (p.module === "report_configuration") return false;
              const moduleConfig = MODULE_PERMISSIONS_CONFIG[p.module];
              return moduleConfig?.has(p.action) ?? true;
            });
            const supportedCount = supportedPerms.length;

            // Calculate selected permissions count (only supported ones)
            const supportedCodes = new Set(supportedPerms.map((p) => p.code));
            const selectedSupportedCount = Array.from(selectedPerms).filter(
              (code) => supportedCodes.has(code),
            ).length;

            return (
              <span className="text-xs d">
                {selectedSupportedCount} of {supportedCount} permissions
                selected
              </span>
            );
          })()}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPermTargetRole(null)}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSavePerms}
              disabled={savingPerms || !isPermsDirty || permsLoading}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20"
            >
              {savingPerms ? "Saving" : "Save changes"}
            </button>
          </div>
        </div>
      </ModalShell>

      {/* ── Edit Role Details Modal ── */}
      <Modal
        open={!!editTargetRole}
        onClose={() => setEditTargetRole(null)}
        title={
          editTargetRole ? `Edit "${editTargetRole.displayName}"` : "Edit Role"
        }
        description="Update this role's display name and description. The role's identifier cannot be changed."
        footer={
          <ModalFooterButtons
            onCancel={() => setEditTargetRole(null)}
            onSubmit={handleEditSave}
            submitLabel={savingEdit ? "Saving" : "Save changes"}
            submitDisabled={savingEdit || !editForm.displayName.trim()}
          />
        }
      >
        {editTargetRole && (
          <>
            <FormField
              label="Role Identifier"
              hint="Cannot be changed after creation"
            >
              <FormInput
                value={editTargetRole.name}
                onChange={() => {}}
                disabled
              />
            </FormField>
            <FormField label="Display Name" required>
              <FormInput
                value={editForm.displayName}
                onChange={(v) => setEditForm((f) => ({ ...f, displayName: v }))}
              />
            </FormField>
            <FormField label="Description">
              <FormTextarea
                value={editForm.description}
                onChange={(v) => setEditForm((f) => ({ ...f, description: v }))}
                placeholder="Describe what this role can do"
              />
            </FormField>
          </>
        )}
      </Modal>

      {/* ── Members Sheet (per mapping) ── */}
      <Sheet
        open={!!memberSheet}
        onOpenChange={(open) => {
          if (!open) setMemberSheet(null);
        }}
      >
        <SheetContent
          side="right"
          className="!max-w-xl sm:!max-w-xl flex flex-col p-0 gap-0"
        >
          {memberSheet && (
            <>
              <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-teal-50 dark:bg-teal-500/10">
                    <Users className="size-5 text-teal-600 dark:text-teal-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <SheetTitle className="truncate text-base">
                      {memberSheet.mapping.groupName
                        .split(",")[0]
                        .replace(/^[Cc][Nn]=/, "")}
                    </SheetTitle>
                    <SheetDescription className="mt-0.5">
                      Members holding{" "}
                      <strong className="text-foreground">
                        {memberSheet.role.displayName}
                      </strong>{" "}
                      in{" "}
                      <strong className="text-foreground">
                        {memberSheet.mapping.orgName}
                      </strong>
                    </SheetDescription>
                  </div>
                </div>
              </SheetHeader>

              <div className="flex-1 overflow-auto p-6">
                {membersLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 className="size-5 animate-spin d" />
                  </div>
                ) : members.length === 0 ? (
                  <EmptyState
                    icon={Users}
                    title="No members"
                    description="No users hold this role in this organization yet. Membership comes from users belonging to the mapped AD group."
                    bordered={false}
                  />
                ) : (
                  <>
                    <p className="text-xs d mb-3">
                      Showing {members.length} user
                      {members.length !== 1 ? "s" : ""} with this role in this
                      org. If multiple AD groups map here, this list reflects
                      users granted by any of them.
                    </p>
                    <div className="rounded-lg border border-border divide-y divide-border bg-card">
                      {members.map((u) => (
                        <div
                          key={u.id}
                          className="flex items-center gap-3 px-4 py-2.5"
                        >
                          <div
                            className={`size-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                              u.isActive
                                ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300"
                                : "bg-muted d"
                            }`}
                          >
                            {u.displayName
                              ?.split(" ")
                              .map((n) => n[0])
                              .join("")
                              .slice(0, 2)
                              .toUpperCase() || "?"}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {u.displayName}
                            </p>
                            <p className="text-xs d truncate">{u.email}</p>
                          </div>
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                              u.isActive
                                ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                                : "bg-slate-100 dark:bg-slate-500/10 text-slate-600 dark:text-slate-400"
                            }`}
                          >
                            {u.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* ── Delete Mapping Confirmation ── */}
      <DeleteConfirmDialog
        open={!!deleteMappingTarget}
        type="AD Group Mapping"
        name={
          deleteMappingTarget
            ? deleteMappingTarget.groupName
                .split(",")[0]
                .replace(/^[Cc][Nn]=/, "")
            : ""
        }
        deleting={deletingMapping}
        showReason={false}
        onCancel={() => setDeleteMappingTarget(null)  }
        onConfirm={() => {
          if (deleteMappingTarget) handleDeleteMapping(deleteMappingTarget.mappingId);
        }}
      />

      {/* ── Delete Role Confirmation ── */}
      <DeleteConfirmDialog
        open={!!deleteRoleTarget}
        type="Role"
        name={deleteRoleTarget?.displayName || ""}
        deleting={deletingId === deleteRoleTarget?.id}
        showReason={false}
        onCancel={() => setDeleteRoleTarget(null)}
        onConfirm={() => {
          if (deleteRoleTarget) handleDeleteRole(deleteRoleTarget);
        }}
      />
    </div>
  );
}
